"use client";

import Image from "next/image";
import Button from "../../ui/Button";
import Link from "next/link";
import Breadcrumbs from "@/components/ui/Breadcrumbs";

/** Art-only PNG at `public/media/pages/why-dank/hero.webp` (not a full-page mockup screenshot). */
const WHY_DANK_HERO_IMAGE = "/media/pages/why-dank/hero.webp";

const STATS = [
  { value: "45,000+", label: "Happy Customers" },
  { value: "120,000+", label: "Orders Delivered" },
  { value: "45 Mins", label: "Average Delivery Time" },
  { value: "500+", label: "Curated Products" },
] as const;

export default function WhyDankHeroSection() {
  return (
    <section
      className="w-full max-w-none"
      aria-labelledby="why-dank-hero-heading"
    >
      <div className="w-full">
        <Breadcrumbs items={[{ label: "Why Dank" }]} />

        <div className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
          <div className="order-2 min-w-0 lg:order-1">
            <h1
              id="why-dank-hero-heading"
              className="text-h2-mobile lg:text-h2-desktop font-bold leading-tight text-text-primary"
            >
              Why Choose Dank?
            </h1>
            <p className="mt-4 text-body-16-desktop leading-relaxed text-text-secondary">
              We prioritize your convenience with self-serve pickup, same-day delivery, and mail-order
              service. From our curated menus to our expert Danktenders, we&apos;re here to meet your every
              need, fast and simple.
            </p>
            <div className="mt-8">
              <Button asChild variant="brand" size="lg" className="w-full sm:w-auto rounded-xl">
                <Link href="/search">
                  Explore our menu
                </Link>
              </Button>
            </div>
          </div>

          <div className="relative order-1 w-full lg:order-2">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-neutral-10 bg-bg-2 shadow-sm sm:rounded-2xl">
              <Image
                src={WHY_DANK_HERO_IMAGE}
                alt="Illustration of Dank retail and delivery experience"
                fill
                className="object-cover object-center"
                sizes="(max-width: 1023px) 100vw, (max-width: 1920px) 50vw, 960px"
                quality={100}
                unoptimized
                priority
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>
        </div>

        <div
          className="mt-12 grid grid-cols-2 gap-6 sm:mt-16 lg:mt-20 lg:grid-cols-4 lg:gap-8"
          aria-label="Dank at a glance"
        >
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="border-t-2 border-pink-200 pt-4 sm:pt-5"
            >
              <p className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary">
                {stat.value}
              </p>
              <p className="mt-1 text-body-14-mobile sm:text-body-14-desktop text-text-secondary">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
